import { handleError } from '@/actions/metaActions';
import { Severity, VegkartError } from '@/state';
import { useMap } from '@/state/context/MapContext';
import { useDispatch } from '@/state/store';
import { download } from '@/utils/Utils';
import { createMapImage } from '../map-utils/createMapImage';
import { useCallback, useEffect, useState } from 'react';

const COPIED_FEEDBACK_MS = 2000;

export const useMapImageActions = () => {
    const { map } = useMap();
    const dispatch = useDispatch();
    const [copyStatus, setCopyStatus] = useState<'idle' | 'copying' | 'copied'>('idle');

    useEffect(() => {
        if (copyStatus !== 'copied') return;
        const timeout = setTimeout(() => setCopyStatus('idle'), COPIED_FEEDBACK_MS);
        return () => clearTimeout(timeout);
    }, [copyStatus]);

    const resetCopyStatus = useCallback(() => setCopyStatus('idle'), []);

    const canvasToBlob = useCallback(() => createMapImage(map), [map]);

    const saveMapImage = useCallback(async () => {
        try {
            const blob = await canvasToBlob();
            const url = URL.createObjectURL(blob);
            download(url, `vegkart-${Date.now()}.png`);
        } catch {
            dispatch(
                handleError(
                    new VegkartError('Kunne ikke lagre kartbildet.', {}, Severity.WARN)
                )
            );
        }
    }, [canvasToBlob, dispatch]);

    const copyMapImage = useCallback(async () => {
        if (!window?.navigator?.clipboard?.write)
            return dispatch(
                handleError(
                    new VegkartError('Støttes ikke av din nettleser.', {}, Severity.WARN)
                )
            );
        setCopyStatus('copying');
        try {
            const blob = await canvasToBlob();
            await window.navigator.clipboard.write([
                new ClipboardItem({
                    [blob.type]: blob,
                }),
            ]);
            setCopyStatus('copied');
        } catch {
            setCopyStatus('idle');
            dispatch(
                handleError(
                    new VegkartError('Kunne ikke kopiere kartbildet.', {}, Severity.WARN)
                )
            );
        }
    }, [canvasToBlob, dispatch]);

    return { copyMapImage, saveMapImage, copyStatus, resetCopyStatus };
};
